import { betaDirectionAPI as directionAPI, thematicsAPI, usersAPI } from '@/api'
import { Config } from '@/config'
import { formateName } from '@/helpers/formateName'
import { jsonConvert } from '@/helpers/jsonConvert'
import { tiptapValueEmpty } from '@/helpers/new/tiptapValueEmpty'
import { z } from 'zod'

export const fetchSeniorSeminarians = async () => {
  //@ts-ignore
  const res = await usersAPI.getUsers({
    filter: ['[role][eq]=senior-seminarian'],
    expand: ['roles'],
    pageSize: false,
  })

  return (
    res.data.data?.map((item: ISeminarian) => ({
      label: formateName(item.name, 'F I'),
      value: item.id.toString(),
    })) ?? []
  )
}

export const fetchFolders = async (): Promise<IFolder[]> => {
  try {
    const res = await thematicsAPI.getAll()
    return res?.data?.data ?? []
  } catch (error) {
    console.error('Ошибка загрузки папок:', error)
    return []
  }
}

export const fetchColleagues = async (name: string): Promise<IColleague[]> => {
  try {
    const res = await usersAPI.getColleaguesForGroup({ name: name })
    return res?.data?.data ?? []
  } catch (error) {
    console.error('Ошибка загрузки колег:', error)
    return []
  }
}

export const fetchTeachers = async () => {
  try {
    const res = await thematicsAPI.getAll()
    return (
      res?.data?.data?.map((item: IFolder) => ({
        label: item.name,
        value: item.id.toString(),
      })) ?? []
    )
  } catch (error) {
    console.error('Ошибка загрузки папок:', error)
    return []
  }
}

export const fetchSubFolders = async (): Promise<ISubFolder[]> => {
  try {
    const res = await thematicsAPI.getAllSubFolders()
    return res?.data?.data ?? []
  } catch (error) {
    console.error('Ошибка загрузки папок:', error)
    return []
  }
}

export const fetchTariffTypes = async () => {
  const customTariffs = [
    'stream_custom_period',
    'stream_custom_days',
    'stream_custom_weeks',
  ]

  try {
    const res = await directionAPI.getTypes()
    return res?.data
      ? Object.keys(res?.data).map((key) => ({
          label: customTariffs.includes(key)
            ? res?.data['stream_custom_period']
            : res?.data[key],
          value: key,
        }))
      : []
  } catch (error) {
    console.error('Ошибка загрузки типов тарифа:', error)
    return []
  }
}

export const fetchCourseSettings = async (id: string) => {
  if (id === 'new') return null

  try {
    const res = await directionAPI.get({
      id,
      expand: [
        'directionTariffs.directionTariffNotices',
        'directionTariffs.tariffPeriods',
        'directionTariffs.tariffWeeks',
        'directionTariffs.tariffDays',
        'seniorSeminarians',
        'mediaIcon',
      ],
    })

    return {
      ...res.data,
      description: jsonConvert(res.data.description, 'json'),
      directionTariffs:
        res.data.directionTariffs?.map((item: ICourseTariff) => ({
          ...item,
          description: jsonConvert(item.description, 'json'),
        })) ?? [],
    }
  } catch (error) {
    console.error('Ошибка загрузки настроек курса:', error)
    return null
  }
}

export const courseDataSchema = z
  .object({
    name: z.string().min(1, 'Название не может быть пустым'),
    short_name: z.string().min(1, 'Короткое название не может быть пустым'),
    description: z.any(),
    filter_id: z.any(),
    start_price: z.any(),
    icon_path: z.any(),
    icon_file: z.any(),
    media_icon_id: z.any(),
  })
  .superRefine((data, ctx) => {
    // if (tiptapValueEmpty(data?.description)) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ['description'],
    //     message: 'Описание должно быть заполнено!',
    //   })
    // }
    if (!data?.filter_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['filter_id'],
        message: 'Нужно выбрать папку курса!',
      })
    }
    if (!data?.start_price) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['start_price'],
        message: 'Начальная цена должна быть указана!',
      })
    }
    if (!(data?.icon_path || data?.icon_file || data?.media_icon_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['icon_path'],
        message: 'Нужно выбрать иконку!',
      })
    }
  })

export const courseTariffDataSchema = z
  .object({
    name: z.string().min(1, 'Название не может быть пустым для тарифа'),
    description: z.any(),
    first_price: z.any(),
    old_price: z.any(),
    type: z.any(),
    visibility_cources: z.any(),
    isAttached_groups: z.any(),
    attached_groups: z.any(),
    tariffPeriods: z.any(),
    tariffDays: z.any(),
    tariffWeeks: z.any(),
  })
  .superRefine((data, ctx) => {
    if (tiptapValueEmpty(data?.description)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['description'],
        message: 'Описание тарифа должно быть заполнено!',
      })
    }
    if (!data?.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type'],
        message: 'Нужно выбрать тип тарифа!',
      })
    }
    if (!data?.visibility_cources) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visibility_cources'],
        message: 'Нужно выбрать тип открытия материалов в курсе!',
      })
    }
    if (['stream_one_time', 'month', 'minute', 'hour'].includes(data?.type)) {
      if (!data?.first_price) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['first_price'],
          message: 'Должна быть указана цена курса в тарифе!',
        })
      }
    }
    if (
      [
        'stream_custom_period',
        'stream_custom_days',
        'stream_custom_weeks',
      ]?.includes(data?.type)
    ) {
      const periods =
        data.type === 'stream_custom_period'
          ? data?.tariffPeriods ?? []
          : data.type === 'stream_custom_days'
          ? data?.tariffDays ?? []
          : data?.tariffWeeks ?? []
      if (Array.isArray(periods) && periods?.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tariffPeriods'],
          message: 'Должен быть указан хотя бы один период для тарифа!',
        })
      }
    }
    if (!['minute', 'hour'].includes(data?.type)) {
      if (data?.isAttached_groups && !data?.attached_groups) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['attached_groups'],
          message: 'Должна быть указана группа для распределения в тарифе!',
        })
      }
    }
  })

export interface IError {
  field: string
  error: string
}

export const saveCourseSettings = async (
  course: ICourse,
): Promise<{
  success: boolean
  errors: IError[]
  course?: ICourse
}> => {
  try {
    const check = courseDataSchema.safeParse(course)

    if (!check.success) {
      const errors = check.error.errors.map((item) => {
        return { field: item.path.toString(), error: item.message }
      })
      return { success: false, errors: errors }
    }

    for (const item of course?.directionTariffs || []) {
      const check = courseTariffDataSchema.safeParse(item)
      if (!check.success) {
        return {
          success: false,
          errors: check.error.errors.map((error) => ({
            field: error.path.toString(),
            error: error.message,
          })),
        }
      }
    }

    const formData = new FormData()

    formData.append('name', course?.name)
    formData.append('short_name', course?.short_name)
    formData.append(
      'description',
      course?.description ? jsonConvert(course?.description) : '',
    )
    formData.append('view', course?.view?.toString())
    if (course?.seniorSeminarians?.length > 0) {
      formData.append(
        'senior_seminarian_ids',
        JSON.stringify(
          course?.seniorSeminarians?.map(({ id }) => id?.toString()),
        ),
      )
    }
    formData.append(
      'permission_add_child',
      (course?.permission_add_child === 1 ? 1 : 0)?.toString(),
    )
    formData.append(
      'flag_easily_creating',
      (course?.flag_easily_creating === 1 ? 1 : 0)?.toString(),
    )
    formData.append(
      'flag_one_block',
      (course?.flag_one_block === 1 ? 1 : 0)?.toString(),
    )
    formData.append(
      'use_promo_code',
      (course?.use_promo_code === 1 ? 1 : 0)?.toString(),
    )
    formData.append(
      'use_discount',
      (course?.use_discount === 1 ? 1 : 0)?.toString(),
    )
    // formData.append('has_demo', (course?.has_demo === 1 ? 1 : 0)?.toString())
    if (course?.duration_in_months) {
      formData.append(
        'duration_in_months',
        course?.duration_in_months?.toString(),
      )
    }
    if (course.filter_id) {
      formData.append('filter_id', course.filter_id?.toString())
    }
    formData.append('start_price', course?.start_price)
    formData.append('old_price', course?.old_price ?? '')
    if (course.icon_file) {
      formData.append('icon_path', course.icon_file)
    }
    if (course.media_icon_id) {
      formData.append('media_icon_id', course.media_icon_id?.toString())
    }
    formData.append(
      'flag_allow_certificate',
      course.flag_allow_certificate?.toString(),
    )
    formData.append(
      'certificate_unlock_percentage',
      course.certificate_unlock_percentage?.toString(),
    )
    if (course.sub_folder_id) {
      formData.append('sub_folder_id', course.sub_folder_id?.toString())
    }
    if (!!course?.directionTariffs?.length) {
      formData.append(
        'tariffs',
        jsonConvert(
          course?.directionTariffs?.map((tariff) => {
            const data: any = {}
            if (tariff.id) data.id = tariff.id
            data.name = tariff.name
            if (tariff.description)
              data.description = jsonConvert(tariff.description)
            data.gpa = tariff.gpa
            data.type = tariff.type
            data.isAttached_groups = tariff.isAttached_groups
            data.visibility_cources = tariff.visibility_cources
            data.direct_link_enabled = tariff?.direct_link_enabled ?? 0
            data.any_seminarian = tariff.any_seminarian
            data.has_paid_cancel = tariff.has_paid_cancel
            data.paid_cancel_student = tariff.paid_cancel_student
            data.paid_cancel_teacher = tariff.paid_cancel_teacher
            data.attached_groups =
              tariff.isAttached_groups === 1 ? tariff.attached_groups : []
            data.view = tariff?.view ?? true
            data.weekly_tariff_text_type = tariff?.weekly_tariff_text_type ?? 0
            if (tariff?.first_price) data.first_price = tariff?.first_price
            if (tariff?.old_price) data.old_price = tariff?.old_price
            if (tariff?.tariffPeriods)
              data.tariffPeriods = tariff?.tariffPeriods
            if (tariff?.tariffDays) data.tariffDays = tariff?.tariffDays
            if (tariff?.tariffWeeks) data.tariffWeeks = tariff?.tariffWeeks
            data.has_demo = tariff.has_demo
            data.demo_days = tariff?.demo_days
            data.custom_id = tariff.custom_id
            data.isCheckboxesView = tariff.isCheckboxesView
            data.auto_chat_settings = tariff.auto_chat_settings

            if (tariff.directionTariffNotices?.length) {
              data.tariff_notices = []

              tariff.directionTariffNotices.map((notice) => {
                const format = {
                  delay_days: notice?.delay_days.toString(),
                  comment: notice?.comment,
                }
                data.tariff_notices.push(format)
              })
            }

            return data
          }),
        ),
      )
    } else {
      formData.append('tariffs', jsonConvert([]))
    }

    const apiCall = course?.id
      ? directionAPI.upd({ id: course?.id, data: formData })
      : directionAPI.add({ data: formData })

    const res = await apiCall
    if (res) {
      return {
        success: res?.data?.status === 'success',
        errors: [],
        course: res?.data?.data,
      }
    }
    return {
      success: false,
      errors: [],
    }
  } catch (error) {
    console.error('Ошибка сохранения курса:', error)
  }
  return { success: false, errors: [] }
}
